"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import { useState, useEffect, useRef } from "react"
import { Modal, Box, Typography, IconButton, Button } from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import { styled } from "@mui/material/styles"
import { useSearchParams } from "next/navigation"
import HIPAAtizerErrorBoundary from "./HIPAAtizerErrorBoundary"

const ModalBox = styled(Box)(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "600px",
  maxHeight: "90vh",
  backgroundColor: "white",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  borderRadius: "12px",
  padding: "24px",
  overflow: "auto",
  "&:focus": {
    outline: "none",
  },
}))

const CloseButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "8px",
  right: "8px",
  color: "#666",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
}))

type Props = {
  open: boolean
  onClose: () => void
  formId: string
  cognitoId?: string | null
  providerId?: number | null
  userId?: number | null
  groupLeaderUserId?: number | null
  groupId?: number | null
  groupName?: string | null
  organizationId?: string | null
  organizationName?: string | null
  isBAAModel?: boolean
}

type EmbedComponent = React.ComponentType<{
  formId: string
  style: React.CSSProperties
}>

export default function TherapistMessageModal({
  open,
  onClose,
  formId,
  cognitoId = null,
  providerId = null,
  userId = null,
  groupLeaderUserId = null,
  groupId = null,
  groupName = null,
  organizationId = null,
  organizationName = null,
  isBAAModel = false,
}: Props) {
  const searchParams = useSearchParams()
  const [HIPAAtizerEmbed, setHIPAAtizerEmbed] = useState<EmbedComponent | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [embedError, setEmbedError] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    if (
      open &&
      (cognitoId ||
        providerId ||
        userId ||
        groupLeaderUserId ||
        groupId ||
        organizationId)
    ) {
      const environment = process.env.NEXT_PUBLIC_ENV || "development"
      const params = new URLSearchParams(searchParams.toString())
      let shouldUpdate = false

      if (
        environment &&
        params.get("environment") !== environment &&
        environment === "development"
      ) {
        params.set("environment", "staging")
        shouldUpdate = true
      }
      if (cognitoId && params.get("cognito_id") !== cognitoId) {
        params.set("cognito_id", cognitoId)
        shouldUpdate = true
      }
      if (providerId && params.get("provider_id") !== String(providerId)) {
        params.set("provider_id", String(providerId))
        shouldUpdate = true
      }
      if (groupId && params.get("group_id") !== String(groupId)) {
        params.set("group_id", String(groupId))
        shouldUpdate = true
      }
      if (groupName && params.get("group_name") !== groupName) {
        params.set("group_name", groupName)
        shouldUpdate = true
      }
      if (
        organizationId &&
        params.get("organization_id") !== String(organizationId)
      ) {
        params.set("organization_id", String(organizationId))
        shouldUpdate = true
      }
      if (organizationName && params.get("organization_name") !== organizationName) {
        params.set("organization_name", organizationName)
        shouldUpdate = true
      }

      if (shouldUpdate) {
        const newUrl = `${window.location.pathname}?${params.toString()}`
        window.history.replaceState({}, "", newUrl)
      }
    }
  }, [
    cognitoId,
    providerId,
    userId,
    open,
    searchParams,
    groupLeaderUserId,
    groupId,
    groupName,
    organizationId,
    organizationName,
  ])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      if (!open) {
        setHIPAAtizerEmbed(null)
        setEmbedError(false)
      }
    }
  }, [open])

  useEffect(() => {
    const loadHIPAAtizerEmbed = async () => {
      try {
        setIsLoading(true)
        setEmbedError(false)

        const hipaatizerModule = await import("@hipaatizer/react-embed")
        let HIPAAtizerEmbedComponent = hipaatizerModule.default as unknown

        if (typeof HIPAAtizerEmbedComponent !== "function") {
          HIPAAtizerEmbedComponent =
            (hipaatizerModule as Record<string, unknown>).HIPAAtizerEmbed ||
            (hipaatizerModule as Record<string, unknown>).default ||
            Object.values(hipaatizerModule).find((val) => typeof val === "function")
        }

        if (typeof HIPAAtizerEmbedComponent === "function") {
          await new Promise((resolve) => setTimeout(resolve, 100))
          if (isMountedRef.current) {
            setHIPAAtizerEmbed(() => HIPAAtizerEmbedComponent as EmbedComponent)
          }
        } else {
          throw new Error("HIPAAtizer component not found in module")
        }
      } catch (_error) {
        if (isMountedRef.current) {
          setEmbedError(true)
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    }

    if (typeof window !== "undefined" && open && formId) {
      void loadHIPAAtizerEmbed()
    }
  }, [formId, open])

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="therapist-message-modal"
      aria-describedby="therapist-message-form"
    >
      <ModalBox>
        <CloseButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </CloseButton>

        <Typography
          variant="h5"
          component="h2"
          sx={{ marginBottom: "20px", fontWeight: 600 }}
        >
          {isBAAModel
            ? "Enable HIPAA-Compliant Messaging"
            : "Send a Message to Your Therapist"}
        </Typography>
        <Typography
          variant="body2"
          sx={{ marginBottom: "24px", color: "#666", lineHeight: 1.5 }}
        >
          {isBAAModel
            ? "Sign the Business Associate Agreement (BAA) with HIPAAtizer to activate secure, confidential communication through your profile and group listings."
            : "Use this secure form to send a message to a therapist. All communications are HIPAA-compliant and confidential."}
        </Typography>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <Typography>Loading secure form...</Typography>
          </Box>
        ) : embedError ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Unable to load secure form
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "#666" }}>
              There was an issue loading the form. Please try again or contact
              support if the problem persists.
            </Typography>
            <Button variant="contained" onClick={() => setEmbedError(false)}>
              Try Again
            </Button>
          </Box>
        ) : HIPAAtizerEmbed ? (
          <Box>
            <HIPAAtizerErrorBoundary onReset={() => setHIPAAtizerEmbed(null)}>
              <HIPAAtizerEmbed
                formId={formId}
                style={{
                  width: "100%",
                  minHeight: "400px",
                  border: "none",
                  borderRadius: "8px",
                }}
              />
            </HIPAAtizerErrorBoundary>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <Typography>Loading...</Typography>
          </Box>
        )}
      </ModalBox>
    </Modal>
  )
}
